import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { reportActions } from '@nxsys/store/slices/report/reducer/reportSlice';
import {
  GetP30ReportParams,
  SaveP30P32SettingParams,
} from '@nxsys/store/slices/report/types';
import { P30P32RecordType } from '@nxsys/types/models/hmrcReports';
import { csvConfigExport } from '@nxsys/utils/export';
import { showToast } from '@nxsys/utils/toast';
import { ExportToCsv } from 'export-to-csv';
import { debounce } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getP30ReportColumns } from './columns';
import ViewP30Modal, { P30FormValues } from '../components/ViewP30Modal';
import ViewP32Modal, { P32FormValues } from '../components/ViewP32Modal';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import AppSelectSync, { OptionType } from '@nxsys/components/AppSelectSync';
import TaxYearSelect from '@nxsys/components/TaxYearSelect';
import {
  useDownloadP30,
  useDownloadP32,
  useSaveP30P32Setting,
} from '@nxsys/hooks/api/report';
import { getTaxWeekTaxYear } from '@nxsys/utils';
import { useNotificationSocket } from '@nxsys/hooks/useNotificationSocket';
import { getP30P32DownloadStatusApi } from '@nxsys/services/api/report';

export type P30P32ReportType = 'P30' | 'P32';

type P30P32FormValues = P30FormValues | P32FormValues;

const REPORT_CONFIG = {
  P30: {
    titleKey: 'report.hmrc.p30.title' as const,
    downloadKey: 'report.hmrc.p30.exportP30' as const,
    exportName: 'HMRC-P30',
    storeKey: 'p30ReportList' as const,
    getListAction: reportActions.getP30ReportList,
  },
  P32: {
    titleKey: 'report.hmrc.p32.title' as const,
    downloadKey: 'report.hmrc.p32.exportP32' as const,
    exportName: 'HMRC-P32',
    storeKey: 'p32ReportList' as const,
    getListAction: reportActions.getP32ReportList,
  },
};

type Props = {
  reportType: P30P32ReportType;
};

const P30P32Report: React.FC<Props> = ({ reportType }) => {
  const config = REPORT_CONFIG[reportType];
  const { messages } = useTypedIntl();

  const dispatch = useDispatch<AppDispatch>();
  const reportList = useSelector(
    (state: AppState) => state.reportStore[config.storeKey],
  );
  const { loading, list, pagination, queryParams } = reportList;
  const { payrollCountry, isBureau, user } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  const [searchValue, setSearchValue] = useState('');
  const [selectedRows, setSelectedRows] = useState<P30P32RecordType[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const downloadResolvedRef = useRef(false);

  const { run: downloadP30Run } = useDownloadP30();
  const { run: downloadP32Run } = useDownloadP32();

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const handleDownloadResult = useCallback(
    (result: { success: boolean; link?: string; error?: string }) => {
      if (downloadResolvedRef.current) return;
      downloadResolvedRef.current = true;
      stopPolling();
      setIsDownloading(false);
      if (result.success && result.link) {
        window.open(result.link, '_blank');
      } else {
        showToast({
          message: result.error || messages['common.error'],
          type: 'error',
        });
      }
    },
    [stopPolling, messages],
  );

  const startPolling = useCallback(
    (jobId: number) => {
      stopPolling();
      const POLL_INTERVAL = 3000;
      const MAX_POLL_TIME = 60000;
      const startTime = Date.now();

      pollingRef.current = setInterval(async () => {
        if (downloadResolvedRef.current) {
          stopPolling();
          return;
        }
        if (Date.now() - startTime > MAX_POLL_TIME) {
          handleDownloadResult({
            success: false,
            error: 'Download timed out. Please try again.',
          });
          return;
        }
        try {
          const status = await getP30P32DownloadStatusApi(jobId);
          if (status.status === 'completed' || status.status === 'failed') {
            handleDownloadResult({
              success: status.success ?? false,
              link: status.link,
              error: status.error,
            });
          }
        } catch {
          // Polling error — keep retrying until timeout
        }
      }, POLL_INTERVAL);
    },
    [stopPolling, handleDownloadResult],
  );

  // Cleanup polling on unmount
  useEffect(() => () => stopPolling(), [stopPolling]);

  useNotificationSocket({
    eventName: `download-P30-P32-process-${user?.id}`,
    onSuccess: (data: { link?: string }) => {
      handleDownloadResult({ success: true, link: data?.link });
    },
    onError: (message) => {
      handleDownloadResult({ success: false, error: message });
    },
  });

  const handleDownload = async (record: P30P32RecordType) => {
    setIsDownloading(true);
    downloadResolvedRef.current = false;
    const taxYear = queryParams?.taxYear ?? currentTaxYear;
    try {
      let response;
      if (reportType === 'P30') {
        response = await downloadP30Run({
          companyId: record.companyRef.id,
          taxMonth: record.taxMonth,
          taxYear,
        });
      } else {
        response = await downloadP32Run({
          companyId: record.companyRef.id,
          taxYear,
        });
      }
      if (response?.jobId) {
        startPolling(response.jobId);
      }
    } catch {
      setIsDownloading(false);
      downloadResolvedRef.current = true;
    }
  };

  const handleFetchData = useCallback(
    (params: GetP30ReportParams) => {
      const newParams = { ...params };
      dispatch(config.getListAction(newParams));
    },
    [dispatch, config.getListAction],
  );

  const currentTaxYear = getTaxWeekTaxYear().taxYear;

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 2,
      taxYear: currentTaxYear,
    });
  }, [currentTaxYear]);

  const debouncedFetchData = useCallback(
    debounce((search: string) => {
      handleFetchData({
        ...queryParams,
        page: 0,
        search: search,
      });
    }, 500),
    [queryParams],
  );

  const exportData = (params: P30P32RecordType[]) => {
    const formattedData = params.map((item) => {
      return {
        companyName: item?.companyRef?.companyName || '',

        taxMonth: item?.taxMonth || '',
        tax: item?.tax?.toFixed(2) || '',

        receivedFromHMRCToRefundTax:
          item?.receivedFromHMRCToRefundTax?.toFixed(2) || '',

        totalCisDeduction: item?.totalCisDeduction?.toFixed(2) || '',

        cisDeductionsSuffered: item?.cisDeductionsSuffered?.toFixed(2) || '',

        grossStudentLoanDeductions:
          item?.grossStudentLoanDeductions?.toFixed(2) || '',

        grossPostgraduateLoanDeductions:
          item?.grossPostgraduateLoanDeductions?.toFixed(2) || '',

        netIncomeTax: item?.netIncomeTax?.toFixed(2) || '',

        grossEmployeeNI: item?.grossEmployeeNI?.toFixed(2) || '',
        grossEmployersNI: item?.grossEmployersNI?.toFixed(2) || '',
        totalStatutoryPayRecovered:
          item?.totalStatutoryPayRecovered?.toFixed(2) || '',

        totalNICCompensation: item?.totalNICCompensation?.toFixed(2) || '',

        receivedFromHMRCToPayStatutoryPay:
          item?.receivedFromHMRCToPayStatutoryPay?.toFixed(2) || '',

        empAllowanceClaimed: item?.empAllowanceClaimed?.toFixed(2) || '',
        appLevyPayable: item?.appLevyPayable?.toFixed(2) || '',

        netNIContributions: item?.netNIContributions?.toFixed(2) || '',

        previousTaxMonthsDebtYtd:
          item?.previousTaxMonthsDebtYtd?.toFixed(2) || '',

        previousTaxMonthsPaidYtd:
          item?.previousTaxMonthsPaidYtd?.toFixed(2) || '',

        previousTaxMonthsShortFall:
          item?.previousTaxMonthsShortFall?.toFixed(2) || '',

        adjustment: item?.adjustment?.toFixed(2) || '',
      };
    });

    const headers = [
      isBureau ? messages['table.employer'] : messages['common.companyName'],

      'Tax Month',
      'Tax',
      'Received From HMRC To Refund Tax',
      'Total CIS Deduction',
      'CIS Deductions Suffered',
      'Gross Student Loan Deductions',
      'Gross Postgraduate Loan Deductions',
      'Net Income Tax',

      'Gross Employee NI',
      'Gross Employers NI',
      'Total Statutory Pay Recovered',
      'Total NIC Compensation',
      'Received From HMRC To Pay Statutory Pay',
      'Employment Allowance Claimed',
      'Apprenticeship Levy Payable',
      'Net NICs',

      'Previous Tax Months Debt YTD',
      'Previous Tax Months Paid YTD',
      'Previous Tax Months Shortfall',
      'Adjustment',
    ];

    const exportName = csvConfigExport(config.exportName, headers);
    const csvExporter = new ExportToCsv(exportName);
    try {
      csvExporter.generateCsv(formattedData);
    } catch (e) {
      console.error('Export failed:', e);
      showToast({ message: messages['common.error'], type: 'error' });
    }
  };

  const handleExportData = () => {
    // P32: export selected rows if the user ticked any, else export the
    // full current view. Never toast — P32 is annual so clicking the
    // Export button without a selection still has an obvious target.
    if (reportType === 'P32') {
      const rows = selectedRows.length ? selectedRows : (list ?? []);
      exportData(rows);
      return;
    }
    // P30: per-month, so a row selection is required.
    if (!selectedRows.length) {
      showToast({
        message: messages['common.noSelected'],
        type: 'warning',
      });
      return;
    }
    exportData(selectedRows);
  };

  const navigate = useNavigateWithQuery();
  const handleGoBack = useCallback(() => navigate('/reports'), [navigate]);

  const renderTitle = useCallback(() => {
    return (
      <div className="flex items-center gap-x-4">
        <AppBackButton onClick={handleGoBack} />
        <AppPageTitle
          prefix={messages[config.titleKey]}
          separator=""
          title=""
        />
      </div>
    );
  }, [messages, handleGoBack, config.titleKey]);

  const handleDownloadSelected = async () => {
    // P32 = one annual PDF per company. Use the company selected in the
    // filter dropdown, falling back to a checked row's company.
    if (reportType === 'P32') {
      const companyId =
        queryParams?.companyIds?.[0] ?? selectedRows[0]?.companyRef?.id;
      if (!companyId) {
        showToast({
          message: messages['form.selectCompany'],
          type: 'warning',
        });
        return;
      }
      setIsDownloading(true);
      downloadResolvedRef.current = false;
      try {
        const response = await downloadP32Run({
          companyId,
          taxYear: queryParams?.taxYear ?? currentTaxYear,
        });
        if (response?.jobId) {
          startPolling(response.jobId);
        }
      } catch {
        setIsDownloading(false);
        downloadResolvedRef.current = true;
      }
      return;
    }
    // P30 = one PDF per tax month; row selection is required.
    if (selectedRows.length !== 1) {
      showToast({
        message: messages['common.noSelected'],
        type: 'warning',
      });
      return;
    }
    await handleDownload(selectedRows[0]);
  };

  const renderRightContent = () => {
    const downloadKey =
      reportType === 'P30'
        ? 'report.hmrc.p30.downloadP30'
        : 'report.hmrc.p32.downloadP32';
    return (
      <div className="flex items-center gap-2">
        <AppButton
          variant="outlined"
          type="primary"
          name={messages[downloadKey]}
          loading={isDownloading}
          onClick={handleDownloadSelected}
        />
        <AppButton
          variant="outlined"
          type="primary"
          name={messages[config.downloadKey]}
          onClick={handleExportData}
        />
      </div>
    );
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedFetchData(value);
  };
  const [openViewModal, setOpenViewModal] = useState(false);
  const [selectedDataToView, setSelectedDataToView] =
    useState<P30P32RecordType | null>(null);

  const handleViewDetail = (record: P30P32RecordType) => {
    setOpenViewModal(true);
    setSelectedDataToView(record);
  };

  const handleChangeTaxMonth = (value: number) => {
    setSelectedDataToView(
      list?.find((item) => item.taxMonth === value) ?? null,
    );
  };

  const { run: saveP30P32SettingRun } = useSaveP30P32Setting();
  const handleSaveP30P32Setting = async (values: P30P32FormValues) => {
    const params: SaveP30P32SettingParams = {
      ...values,
      netIncomeTax: values.netIncomeTax ?? 0,
      netNIContributions: values.netNIContributions ?? 0,
      netAdjustment: values.netAdjustment ?? 0,
      dueInCurrentPeriod: values.dueInCurrentPeriod ?? 0,
      companyId: selectedDataToView!.companyRef.id,
      taxMonth: selectedDataToView!.taxMonth,
    };
    const response = await saveP30P32SettingRun(params);
    if (response.success) {
      showToast({
        message: messages['report.hmrc.p30.saveSuccess'],
        type: 'success',
      });
    }
  };

  const buildDefaultValues = (data: P30P32RecordType) => ({
    tax: Number(Number(data?.tax ?? 0)?.toFixed(2)),
    receivedFromHMRCToRefundTax: Number(
      Number(data?.receivedFromHMRCToRefundTax ?? 0)?.toFixed(2),
    ),
    totalCisDeduction: Number(Number(data?.totalCisDeduction ?? 0)?.toFixed(2)),
    cisDeductionsSuffered: Number(
      Number(data?.cisDeductionsSuffered ?? 0)?.toFixed(2),
    ),
    grossStudentLoanDeductions: Number(
      Number(data?.grossStudentLoanDeductions ?? 0)?.toFixed(2),
    ),
    grossPostgraduateLoanDeductions: Number(
      Number(data?.grossPostgraduateLoanDeductions ?? 0)?.toFixed(2),
    ),
    grossEmployeeNI: Number(Number(data?.grossEmployeeNI ?? 0)?.toFixed(2)),
    grossEmployersNI: Number(Number(data?.grossEmployersNI ?? 0)?.toFixed(2)),
    totalStatutoryPayRecovered: Number(
      Number(data?.totalStatutoryPayRecovered ?? 0)?.toFixed(2),
    ),
    totalNICCompensation: Number(
      Number(data?.totalNICCompensation ?? 0)?.toFixed(2),
    ),
    receivedFromHMRCToPayStatutoryPay: Number(
      Number(data?.receivedFromHMRCToPayStatutoryPay ?? 0)?.toFixed(2),
    ),
    empAllowanceClaimed: Number(
      Number(data?.empAllowanceClaimed ?? 0)?.toFixed(2),
    ),
    appLevyPayable: Number(Number(data?.appLevyPayable ?? 0)?.toFixed(2)),
    previousTaxMonthsPaidYtd: Number(
      Number(data?.previousTaxMonthsPaidYtd ?? 0)?.toFixed(2),
    ),
    previousTaxMonthsDebtYtd: Number(
      Number(data?.previousTaxMonthsDebtYtd ?? 0)?.toFixed(2),
    ),
    previousTaxMonthsShortFall: Number(
      Number(data?.previousTaxMonthsShortFall ?? 0)?.toFixed(2),
    ),
    adjustment: Number(Number(data?.adjustment ?? 0)?.toFixed(2)),
    currentTaxMonthPaid: Number(
      Number(data?.currentTaxMonthPaid ?? 0)?.toFixed(2),
    ),
    paymentDate: data?.paymentDate ?? '',
  });

  const isFiltering = !!queryParams?.search || !!queryParams?.companyIds;

  const ViewModal = reportType === 'P30' ? ViewP30Modal : ViewP32Modal;

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
    >
      <AppCard>
        <AppsHeader>
          <div className="flex items-center justify-between w-full gap-x-4">
            <div className="flex items-center gap-x-4">
              <AppSelectSync
                type="company"
                apiType="searchCompany"
                placeholder={messages['form.selectCompany']}
                onChangeValue={(option) => {
                  handleFetchData({
                    ...queryParams,
                    page: 0,
                    companyIds: (option as OptionType)?.value
                      ? [(option as OptionType).value]
                      : undefined,
                  });
                }}
              />
              <TaxYearSelect
                onFilter={(value) =>
                  handleFetchData({
                    ...queryParams,
                    page: 0,
                    taxYear: value.taxYear,
                  })
                }
              />
            </div>
            <AppInputSearch value={searchValue} onChange={handleSearchChange} />
          </div>
        </AppsHeader>
        <AppsContent>
          <AppTableContainer
            rowKey="key"
            data={
              list?.map((item, index) => ({
                ...item,
                key: index,
              })) ?? []
            }
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            pageSizeOptions={[2, 5, 10, 20, 50, 100, 200]}
            scroll={{ x: 1200, y: 700 }}
            titleEmpty={messages['assignment.empty']}
            columns={getP30ReportColumns({
              messages,
              isIreland,
              handleViewDetail,
              payrollCountry,
              isBureau,
            })}
            rowSelection={{
              type: 'checkbox',
              onChange: (_: React.Key[], selectedRows: any[]) => {
                setSelectedRows(selectedRows);
              },
            }}
            onChangePagination={onChangePagination}
            noDataType={isFiltering ? 'noDataFound' : 'noDataToShow'}
          />
        </AppsContent>
      </AppCard>
      {openViewModal && selectedDataToView && (
        <ViewModal
          open={openViewModal}
          onClose={() => setOpenViewModal(false)}
          data={selectedDataToView}
          defaultValues={buildDefaultValues(selectedDataToView)}
          onChangeTaxMonth={handleChangeTaxMonth}
          onSave={handleSaveP30P32Setting}
          onExport={exportData}
          onDownload={handleDownload}
          isDownloading={isDownloading}
        />
      )}
    </AppsContainer>
  );
};

export default P30P32Report;
